// Mock API for topology data
import { telemetry, application } from "globals/endpoints";

// Helper function to generate deterministic UUID from a seed (for consistent mock data)
function generateDeterministicUUID(seed) {
  // Create a simple deterministic hash from the seed
  let hash = 0;
  const str = String(seed);
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert hash to UUID format
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  return `${hex.slice(0,8)}-${hex.slice(0,4)}-4${hex.slice(0,3)}-${hex.slice(0,4)}-${hex.slice(0,12)}`;
}

// Fetch SVG templates for node types
export function fetchSvgTemplates() {  
  return telemetry
    .get('/category/list')
    .then((response) => {      
      const apiData = response.data.data || response.data;
      
      // Map API response: code (attribute) -> { svgIcon, managementURL }
      const templates = {};
      apiData.forEach(item => {
        if (item.code) {
          templates[item.code] = {
            svgIcon: item.svgIcon || null,
            managementURL: item.managementURL || null,
            hasManagementInterface: item.hasManagementInterface || false
          };
        }
      });
      
      return templates;
    })
    .catch((error) => {
      console.error('❌ Failed to fetch SVG templates from API:', error);
      // Return empty object - will use fallback templates
      return {};
    });
}

// Fetch severity color configuration
export function fetchSeverityColors() {  
  // Define full range fallback data (0-21)
  const fallbackColors = [
    { id: 0, severity: "NONE", color: "#4caf50" }, // Green for healthy/okay nodes
    { id: 1, severity: "INFO", color: "#9e9e9e" },
    { id: 2, severity: "WARNING", color: "#00a2ff" },
    { id: 3, severity: "MINOR", color: "#ffeb3b" },
    { id: 4, severity: "MAJOR", color: "#ff9100" },
    { id: 5, severity: "CRITICAL", color: "#ff1744" },
    // Extended severity levels (6-21) - progressively darker reds
    { id: 6, severity: "SEVERITY_6", color: "#f50057" },
    { id: 7, severity: "SEVERITY_7", color: "#d32f2f" },
    { id: 8, severity: "SEVERITY_8", color: "#d81b60" },
    { id: 9, severity: "SEVERITY_9", color: "#c2185b" },
    { id: 10, severity: "SEVERITY_10", color: "#ad1457" },
    { id: 11, severity: "SEVERITY_11", color: "#880e4f" },
    { id: 12, severity: "SEVERITY_12", color: "#d32f2f" },
    { id: 13, severity: "SEVERITY_13", color: "#c62828" },
    { id: 14, severity: "SEVERITY_14", color: "#b71c1c" },
    { id: 15, severity: "SEVERITY_15", color: "#a50000" },
    { id: 16, severity: "SEVERITY_16", color: "#940000" },
    { id: 17, severity: "SEVERITY_17", color: "#830000" },
    { id: 18, severity: "SEVERITY_18", color: "#720000" },
    { id: 19, severity: "SEVERITY_19", color: "#610000" },
    { id: 20, severity: "SEVERITY_20", color: "#500000" },
    { id: 21, severity: "SEVERITY_21", color: "#3f0000" }
  ];
  
  return application
    .post('/severity/listAll')
    .then((response) => {
      
      // API response structure: { id, severityLevel, severityName, color }
      // Map to frontend format: { id: severityLevel, severity: severityName, color }
      const apiData = response.data.data || response.data;
      
      const apiColors = apiData.map(item => ({
        id: item.severityLevel,
        severity: item.severityName,
        color: item.color
      }));
            
      // Merge API data with fallback - API data takes precedence where it exists
      const mergedColors = fallbackColors.map(fallback => {
        const apiOverride = apiColors.find(c => c.id === fallback.id);
        return apiOverride || fallback;
      });
            
      return mergedColors;
    })
    .catch((error) => {
      console.error('❌ Failed to fetch severity colors from API:', error);
      // Return default severity colors as fallback
      // Higher numbers = more severe (0-21 range)
      return fallbackColors;
    });
}

// Fetch station-level nodes (top level view) from API
export function fetchStationNodes() {  
  return telemetry
    .get('/link-view/nodes/top-level/all')
    .then((response) => {
      
      // API response structure: { issue, status, message, data: [...] }
      // Normalize the response to match expected format
      const apiNodes = response.data.data || response.data.nodes || response.data;
      
      const nodes = apiNodes.map(node => {
        // Parse alarms: convert severity from string to number
        const alarms = (node.alarms || []).map(alarm => ({
          severity: parseInt(alarm.severity, 10),
          text: alarm.text
        }));
        
        // Use severity directly from API (already accumulated/calculated by backend)
        const nodeSeverity = node.severity !== undefined ? node.severity : 0;
        
        return {
          id: node.id,
          type: node.type,
          parent: node.parent,
          label: node.label,
          status: node.status === true ? "up" : "down", // Convert boolean to string
          severity: nodeSeverity, // Use API-provided severity
          alarms: alarms,
          hasChildren: node.hasChildren,
          position_x: node.positionX, // Convert camelCase to snake_case
          position_y: node.positionY,  // Convert camelCase to snake_case
          location: node.location || null,
          childrenSeverity: node.childrenSeverity || 0,
          ipAddress: node.ipAddress || null
        };
      });
      
      return { nodes };
    })
    .catch((error) => {
      console.error('❌ Failed to fetch top-level nodes from API:', error);
      throw error;
    });
}

// Fetch child nodes for a specific location from API
export function fetchChildNodes(location) {  
  return telemetry
    .get(`/link-view/nodes/children/${location}`)
    .then((response) => {      
      // API response structure: { issue, status, message, data: [...] } where data is array of nodes
      const apiData = response.data.data || response.data;
      
      // Check if apiData is an array (nodes directly) or an object with nodes property
      let apiNodes = [];
      let parentStation = { id: location, label: `Station ${location}` };
      
      if (Array.isArray(apiData)) {
        // Data is directly an array of nodes
        apiNodes = apiData;
      } else if (apiData.nodes && Array.isArray(apiData.nodes)) {
        // Data is an object with nodes property
        apiNodes = apiData.nodes;
        parentStation = apiData.parentStation || parentStation;
      }
      
      const nodes = apiNodes.map(node => {
        // Parse alarms: convert severity from string to number
        const alarms = (node.alarms || []).map(alarm => ({
          severity: parseInt(alarm.severity, 10),
          text: alarm.text
        }));
        
        // Use severity directly from API (already accumulated/calculated by backend)
        const nodeSeverity = node.severity !== undefined ? node.severity : 0;
        
        return {
          id: node.id,
          type: node.type,
          parent: node.parent,
          label: node.label,
          status: node.status === true ? "up" : "down", // Convert boolean to string
          severity: nodeSeverity, // Use API-provided severity
          alarms: alarms,
          hasChildren: node.hasChildren || false,
          position_x: node.positionX, // Convert camelCase to snake_case
          position_y: node.positionY,  // Convert camelCase to snake_case
          location: node.location || null,
          childrenSeverity: node.childrenSeverity || 0,
          ipAddress: node.ipAddress || null
        };
      });
      
      return { 
        nodes,
        parentStation
      };
    })
    .catch((error) => {
      console.error(`❌ Failed to fetch child nodes for location ${location}:`, error);
      throw error;
    });
}

// Fetch real-time status updates for nodes
// Calls fetchStationNodes or fetchChildNodes based on view mode
export async function fetchStatusUpdate(nodeIds, viewMode = "top-level", location = null) {  
  try {
    let nodes;
    
    if (viewMode === "top-level") {
      // Fetch top-level nodes
      const response = await fetchStationNodes();
      nodes = response.nodes;
    } else {
      // Fetch child nodes for specific location
      const response = await fetchChildNodes(location);
      nodes = response.nodes;
    }
    
    // Create a map of nodeId -> {status, severity, alarms} for requested nodes
    const updates = {};
    
    nodes.forEach(node => {
      // Only include nodes that were requested
      if (nodeIds.includes(node.id)) {
        updates[node.id] = {
          status: node.status,
          severity: node.severity !== undefined ? node.severity : 0,
          alarms: node.alarms,
          childrenSeverity: node.childrenSeverity || 0,
          ipAddress: node.ipAddress || null
        };
      }
    });
    
    return updates;
  } catch (error) {
    console.error('❌ Failed to fetch status updates:', error);
    // Return empty updates on error
    return {};
  }
}

// Save node position to backend
export function saveNodePosition(nodeId, position, viewMode, assetId = null) {
  const payload = {
    nodeId,
    position: {
      x: Math.round(position.x * 100) / 100, // Round to 2 decimals
      y: Math.round(position.y * 100) / 100
    },
    viewMode,
    assetId,
    timestamp: new Date().toISOString()
  };

  return telemetry
    .post('/preferences/node-position', payload)
    .then((response) => {
      return { success: true, data: response.data };
    })
    .catch((error) => {
      console.error('Failed to save node position:', error);
      return { success: false, error: error.message };
    });
}

// Save view preferences (node size, font, zoom, label settings, pan)
export function saveViewPreferences(preferences, viewMode, assetId = null) {
  const payload = {
    preferences: {
      nodeSize: preferences.nodeSize,
      fontSize: preferences.fontSize,
      zoomLevel: preferences.zoomLevel,
      labelPosition: preferences.labelPosition,
      labelDistance: preferences.labelDistance,
      pan: preferences.pan // { x, y }
    },
    viewMode, // "top-level" or "children"
    assetId, // null for top-level view, asset ID for children view
    timestamp: new Date().toISOString()
  };

  return telemetry
    .post('/preferences/link-view', payload)
    .then((response) => {
      return { success: true, data: response.data };
    })
    .catch((error) => {
      console.error('Failed to save view preferences:', error);
      return { success: false, error: error.message };
    });
}

// Fetch view preferences from backend
export function fetchViewPreferences(viewMode, assetId = null) {
  const params = {
    viewMode,
    ...(assetId && { assetId })
  };

  return telemetry
    .get('/preferences/link-view', { params })
    .then((response) => {
      return response.data.data;
    })
    .catch((error) => {
      // Fall back to defaults if API call fails
      const defaults = {
        nodeSize: 85,
        fontSize: 14,
        zoomLevel: 1,
        labelPosition: "above",
        labelDistance: 40,
        pan: null
      };
      return {
        preferences: defaults,
        viewMode,
        assetId
      };
    });
}

// Clear all node positions (reset to default layout)
export function clearNodePositions(viewMode, location = null) {

  // Determine the correct API endpoint based on view mode
  const endpoint = viewMode === "top-level" 
    ? '/link-view/nodes/top-level/all'
    : `/link-view/nodes/children/${location}`;

  return telemetry
    .delete(endpoint)
    .then((response) => {
      return { success: true, data: response.data };
    })
    .catch((error) => {
      console.error('Failed to clear node positions:', error);
      return { success: false, error: error.message };
    });
}
